import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  FiUploadCloud, FiCalendar, FiUsers, FiCheckCircle,
  FiXCircle, FiAlertCircle, FiTrendingUp, FiBarChart2,
  FiFileText, FiX, FiClock, FiRefreshCw
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import './HrAttendance.css';

const api = axios.create({ baseURL: process.env.REACT_APP_BASE_URL });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const fmtDateTime = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

/* ── Import History Table ───────────────────────────────── */
function ImportHistory({ refresh }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await api.get('/attendance/import-logs');
      setLogs(res.data || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchLogs(); }, [refresh]);

  if (loading) return (
    <div className="flex items-center justify-center py-8 text-slate-400 text-sm gap-2">
      <FiRefreshCw className="animate-spin" size={14} /> Loading history...
    </div>
  );

  if (logs.length === 0) return (
    <div className="text-center py-8 text-slate-400 text-sm">No uploads yet.</div>
  );

  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
          <tr>
            <th className="px-4 py-3 text-left">File</th>
            <th className="px-4 py-3 text-left">Date</th>
            <th className="px-4 py-3 text-left">Uploaded By</th>
            <th className="px-4 py-3 text-center">Rows</th>
            <th className="px-4 py-3 text-center">New</th>
            <th className="px-4 py-3 text-center">Updated</th>
            <th className="px-4 py-3 text-center">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 bg-white">
          {logs.map((log) => (
            <tr key={log.id} className="hover:bg-slate-50 transition-colors">
              <td className="px-4 py-3 text-slate-700 font-medium max-w-[200px] truncate" title={log.fileName}>
                <div className="flex items-center gap-2">
                  <FiFileText className="text-slate-400 shrink-0" size={13} />
                  <span className="truncate">{log.fileName}</span>
                </div>
              </td>
              <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                <div className="flex items-center gap-1.5">
                  <FiClock size={12} className="text-slate-400" />
                  {fmtDateTime(log.uploadedAt)}
                </div>
              </td>
              <td className="px-4 py-3 text-slate-600">{log.uploadedBy}</td>
              <td className="px-4 py-3 text-center text-slate-700 font-medium">{log.totalRows ?? '—'}</td>
              <td className="px-4 py-3 text-center text-emerald-600 font-semibold">{log.newRecords ?? '—'}</td>
              <td className="px-4 py-3 text-center text-amber-600 font-semibold">{log.updatedRecords ?? '—'}</td>
              <td className="px-4 py-3 text-center">
                <span className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide
                  ${log.status === 'SUCCESS' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                  {log.status}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── Daily Upload Panel ─────────────────────────────────── */
function DailyUploadPanel({ onUploaded }) {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('present');

  const handleFileChange = (e) => {
    if (e.target.files?.[0]) {
      const f = e.target.files[0];
      if (f.type !== 'application/pdf') { toast.error('Please upload a valid PDF file.'); return; }
      setFile(f);
    }
  };

  const handleUpload = async () => {
    if (!file) { toast.error('Please select a file first.'); return; }
    const formData = new FormData();
    formData.append('pdfFile', file);
    try {
      setUploading(true);
      const res = await api.post('/attendance/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Attendance report processed successfully!');
      setSummary(res.data);
      setSelectedDate(res.data.attendanceDate);
      setFile(null);
      onUploaded?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to upload attendance report.');
    } finally { setUploading(false); }
  };

  const fetchSummary = async (date) => {
    try {
      setLoading(true);
      const res = await api.get(`/attendance/summary/${date}`);
      setSummary(res.data);
    } catch { setSummary(null); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchSummary(selectedDate); }, [selectedDate]);

  const renderTable = (employees, type) => {
    if (!employees?.length) return <div className="empty-state"><FiUsers className="empty-icon" /><p>No {type} employees.</p></div>;
    return (
      <div className="attendance-table-container">
        <table className="attendance-table">
          <thead><tr><th>ID</th><th>Employee Name</th></tr></thead>
          <tbody>{employees.map((emp, i) => <tr key={i}><td>{emp.attendanceId}</td><td>{emp.employeeName}</td></tr>)}</tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="space-y-5">
      {/* Date + upload row */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="date-picker-wrapper flex-1 min-w-[160px]">
          <FiCalendar className="date-icon" />
          <input type="date" className="date-input" value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)} max={new Date().toISOString().split('T')[0]} />
        </div>
        <div className="upload-section glass-panel flex-1" style={{ margin: 0 }}>
          <div className="upload-content">
            <div className="file-input-wrapper">
              <input type="file" accept="application/pdf" onChange={handleFileChange}
                className="hidden-input" id="pdf-upload-daily" />
              <label htmlFor="pdf-upload-daily" className="upload-label">
                <FiUploadCloud className="upload-icon" />
                <div className="upload-text">
                  <span className="highlight-text">Click to upload</span> or drag and drop
                  <p className="sub-text">eSSL Daily PDF (Max 10MB)</p>
                </div>
              </label>
              {file && <div className="selected-file"><span>{file.name}</span><button className="remove-file" onClick={() => setFile(null)}>×</button></div>}
            </div>
            <button className={`btn-primary upload-btn ${uploading ? 'loading' : ''}`} onClick={handleUpload} disabled={!file || uploading}>
              {uploading ? 'Processing...' : 'Upload'}
            </button>
          </div>
        </div>
      </div>

      {/* Summary */}
      {loading ? (
        <div className="loading-state"><div className="spinner" /><p>Loading...</p></div>
      ) : summary ? (
        <div className="summary-dashboard">
          <div className="metrics-grid">
            {[
              { label: 'Total', value: summary.totalEmployees, icon: <FiUsers />, cls: 'gradient-blue' },
              { label: 'Present', value: summary.presentCount, icon: <FiCheckCircle />, cls: 'gradient-green' },
              { label: 'Absent', value: summary.absentCount, icon: <FiXCircle />, cls: 'gradient-red' },
              { label: 'Half Day', value: summary.halfPresentCount, icon: <FiAlertCircle />, cls: 'gradient-orange' },
              { label: 'New / Updated', value: `${summary.newRecords} / ${summary.updatedRecords}`, icon: <FiTrendingUp />, cls: 'gradient-purple' },
              { label: 'Unmapped', value: summary.unmappedUsers, icon: <FiUsers />, cls: 'gradient-gray' },
            ].map(({ label, value, icon, cls }) => (
              <div key={label} className={`metric-card ${cls}`}>
                <div className="metric-icon">{icon}</div>
                <div className="metric-info"><h3>{label}</h3><p className="metric-value">{value}</p></div>
              </div>
            ))}
          </div>
          <div className="details-section glass-panel mt-8">
            <div className="tabs-container">
              {[['present', `Present (${summary.presentCount})`], ['absent', `Absent (${summary.absentCount})`], ['half', `Half Day (${summary.halfPresentCount})`]].map(([id, label]) => (
                <button key={id} className={`tab-btn ${activeTab === id ? 'active' : ''}`} onClick={() => setActiveTab(id)}>{label}</button>
              ))}
            </div>
            <div className="tab-content">
              {activeTab === 'present' && renderTable(summary.presentEmployees, 'present')}
              {activeTab === 'absent' && renderTable(summary.absentEmployees, 'absent')}
              {activeTab === 'half' && renderTable(summary.halfPresentEmployees, 'half present')}
            </div>
          </div>
        </div>
      ) : (
        <div className="empty-state glass-panel mt-8">
          <FiCalendar className="empty-icon text-slate-300" />
          <p>No data for {selectedDate}. Upload an eSSL report.</p>
        </div>
      )}
    </div>
  );
}

/* ── Monthly Upload Panel ───────────────────────────────── */
function MonthlyUploadPanel({ onUploaded }) {
  const [file, setFile]           = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult]       = useState(null);
  const fileInputRef              = useRef(null);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.type !== 'application/pdf') { toast.error('Please upload a valid PDF file.'); return; }
    setFile(f); setResult(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f?.type === 'application/pdf') { setFile(f); setResult(null); }
    else toast.error('Please drop a valid PDF file.');
  };

  const clearFile = (e) => {
    e.stopPropagation();
    setFile(null); setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUpload = async () => {
    if (!file) { toast.error('Please select a file first.'); return; }
    const formData = new FormData();
    formData.append('pdfFile', file);
    try {
      setUploading(true);
      const res = await api.post('/attendance/upload-monthly', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Monthly report imported!');
      setResult(res.data);
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      onUploaded?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to process monthly report.');
    } finally { setUploading(false); }
  };

  return (
    <div className="space-y-5">
      {/* Drop zone */}
      <div
        className={`relative border-2 border-dashed rounded-2xl p-7 text-center cursor-pointer transition-all
          ${file ? 'border-emerald-400 bg-emerald-50/60' : 'border-slate-300 bg-slate-50 hover:border-blue-400 hover:bg-blue-50/30'}`}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input ref={fileInputRef} type="file" accept="application/pdf"
          onChange={handleFileChange} className="hidden" id="pdf-upload-monthly" />

        {file ? (
          <div className="flex flex-col items-center gap-2">
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
              <FiFileText className="text-emerald-600 text-xl" />
            </div>
            <p className="font-semibold text-slate-800 text-sm">{file.name}</p>
            <p className="text-xs text-slate-400">{(file.size / 1024).toFixed(1)} KB</p>
            <button className="text-xs text-red-400 hover:text-red-600 flex items-center gap-1 mt-1" onClick={clearFile}>
              <FiX size={11} /> Remove
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <div className="w-12 h-12 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center">
              <FiUploadCloud className="text-slate-400 text-2xl" />
            </div>
            <p className="text-sm text-slate-600"><span className="text-blue-600 font-semibold">Click to upload</span> or drag & drop</p>
            <p className="text-xs text-slate-400">Monthly Status Report (Basic Report) PDF</p>
          </div>
        )}
      </div>

      <button
        onClick={handleUpload}
        disabled={!file || uploading}
        className={`w-full py-2.5 rounded-xl font-semibold text-sm transition-all
          ${!file || uploading ? 'bg-slate-200 text-slate-400 cursor-not-allowed' : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm'}`}
      >
        {uploading ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
            </svg> Importing...
          </span>
        ) : 'Import Monthly Report'}
      </button>

      {/* Result summary */}
      {result && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="bg-indigo-600 px-5 py-3 text-white">
            <p className="text-xs font-medium opacity-80 mb-0.5">Import Complete</p>
            <p className="font-bold">{fmtDate(result.startDate)} → {fmtDate(result.endDate)}</p>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-6 divide-x divide-slate-100">
            {[
              { label: 'Days',      value: result.totalDays,             color: 'text-indigo-600' },
              { label: 'Employees', value: result.totalEmployees,        color: 'text-blue-600' },
              { label: 'Records',   value: result.totalRecordsProcessed, color: 'text-slate-700' },
              { label: 'New',       value: result.newRecords,            color: 'text-emerald-600' },
              { label: 'Updated',   value: result.updatedRecords,        color: 'text-amber-600' },
              { label: 'Unmapped',  value: result.unmappedEmployees,     color: result.unmappedEmployees > 0 ? 'text-red-500' : 'text-slate-400' },
            ].map(({ label, value, color }) => (
              <div key={label} className="px-3 py-3 text-center">
                <div className={`text-xl font-extrabold ${color}`}>{value}</div>
                <div className="text-[10px] text-slate-500 font-medium mt-0.5">{label}</div>
              </div>
            ))}
          </div>
          {result.unmappedEmployees > 0 && (
            <div className="mx-4 mb-3 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700">
              <FiAlertCircle size={13} className="shrink-0" />
              {result.unmappedEmployees} emp code(s) not matched — set Attendance ID in their profile.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Admin Attendance Records Viewer & Clear Database ─────────────── */
function MonthlyAttendanceViewer({ refreshKey }) {
  const getCurrentMonthStr = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  };

  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonthStr());
  const [selectedDateFilter, setSelectedDateFilter] = useState('');
  const [selectedDeptFilter, setSelectedDeptFilter] = useState('ALL');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('ALL');
  const [searchEmpQuery, setSearchEmpQuery] = useState('');
  
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const [clearing, setClearing] = useState(false);

  const getMonthDateRange = (monthStr) => {
    if (!monthStr) return { start: '', end: '' };
    const [year, month] = monthStr.split('-').map(Number);
    const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
    const lastDay = new Date(year, month, 0).getDate();
    const endDate = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    return { start: startDate, end: endDate };
  };

  const fetchMonthlyRecords = async () => {
    setLoading(true);
    try {
      const { start, end } = getMonthDateRange(selectedMonth);
      const res = await api.get(`/attendance/range?startDate=${start}&endDate=${end}`);
      setRecords(res.data || []);
    } catch (err) {
      toast.error('Failed to load attendance records.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMonthlyRecords();
  }, [selectedMonth, refreshKey]);

  // Extract unique departments for filter dropdown
  const departments = ['ALL', ...Array.from(new Set(records.map(r => r.department).filter(Boolean)))];

  const filteredRecords = records.filter(r => {
    // Date filter
    if (selectedDateFilter && r.attendanceDate !== selectedDateFilter) return false;
    
    // Dept filter
    if (selectedDeptFilter !== 'ALL' && (r.department || '').toLowerCase() !== selectedDeptFilter.toLowerCase()) return false;

    // Status filter
    if (selectedStatusFilter !== 'ALL') {
      const st = (r.status || '').toUpperCase().replace(/ /g, '_');
      if (selectedStatusFilter === 'PRESENT' && st !== 'PRESENT' && st !== 'WEEK_OFF_PRESENT') return false;
      if (selectedStatusFilter === 'ABSENT' && st !== 'ABSENT') return false;
      if (selectedStatusFilter === 'HALF_PRESENT' && st !== 'HALF_PRESENT') return false;
      if (selectedStatusFilter === 'WEEK_OFF' && st !== 'WEEK_OFF') return false;
      if (selectedStatusFilter === 'LEAVE' && st !== 'LEAVE' && st !== 'HOLIDAY') return false;
    }

    // Employee Name / ID search
    if (searchEmpQuery.trim()) {
      const q = searchEmpQuery.toLowerCase();
      const matchName = (r.employeeName || '').toLowerCase().includes(q);
      const matchId = (r.attendanceId || '').toLowerCase().includes(q);
      if (!matchName && !matchId) return false;
    }

    return true;
  });

  return (
    <div className="space-y-4">
      {/* Header & Filter Toolbar */}
      <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-4 pb-3 border-b border-slate-100">
          <div>
            <h2 className="text-base font-bold text-slate-800 flex items-center gap-2">
              <FiBarChart2 className="text-indigo-600" /> Attendance Records
            </h2>
            <p className="text-xs text-slate-500">Filter attendance by month, date, employee name/ID, department, or status</p>
          </div>
        </div>

        {/* Clean Filter Controls Bar */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 text-xs">
          {/* 1. Month Filter */}
          <div className="space-y-1">
            <label className="font-semibold text-slate-600 flex items-center gap-1">
              <FiCalendar className="text-slate-400" /> Month
            </label>
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => { setSelectedMonth(e.target.value); setSelectedDateFilter(''); }}
              className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium text-slate-800 outline-none focus:bg-white focus:border-indigo-500 transition-all"
            />
          </div>

          {/* 2. Specific Date Filter */}
          <div className="space-y-1">
            <label className="font-semibold text-slate-600 flex items-center gap-1">
              <FiClock className="text-slate-400" /> Specific Date
            </label>
            <input
              type="date"
              value={selectedDateFilter}
              onChange={(e) => setSelectedDateFilter(e.target.value)}
              className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium text-slate-800 outline-none focus:bg-white focus:border-indigo-500 transition-all"
            />
          </div>

          {/* 3. Employee Search */}
          <div className="space-y-1">
            <label className="font-semibold text-slate-600 flex items-center gap-1">
              <FiUsers className="text-slate-400" /> Employee / ID
            </label>
            <input
              type="text"
              placeholder="Search name or ID..."
              value={searchEmpQuery}
              onChange={(e) => setSearchEmpQuery(e.target.value)}
              className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs outline-none focus:bg-white focus:border-indigo-500 transition-all"
            />
          </div>

          {/* 4. Department Filter */}
          <div className="space-y-1">
            <label className="font-semibold text-slate-600">Department</label>
            <select
              value={selectedDeptFilter}
              onChange={(e) => setSelectedDeptFilter(e.target.value)}
              className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium text-slate-800 outline-none focus:bg-white focus:border-indigo-500 transition-all"
            >
              {departments.map(d => (
                <option key={d} value={d}>{d === 'ALL' ? 'All Departments' : d}</option>
              ))}
            </select>
          </div>

          {/* 5. Status Filter */}
          <div className="space-y-1">
            <label className="font-semibold text-slate-600">Status</label>
            <select
              value={selectedStatusFilter}
              onChange={(e) => setSelectedStatusFilter(e.target.value)}
              className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium text-slate-800 outline-none focus:bg-white focus:border-indigo-500 transition-all"
            >
              <option value="ALL">All Statuses</option>
              <option value="PRESENT">Present</option>
              <option value="ABSENT">Absent</option>
              <option value="HALF_PRESENT">Half Present</option>
              <option value="WEEK_OFF">Week Off</option>
              <option value="LEAVE">Leave / Holiday</option>
            </select>
          </div>
        </div>

        {/* Active Filter Pills & Reset */}
        {(selectedDateFilter || selectedDeptFilter !== 'ALL' || selectedStatusFilter !== 'ALL' || searchEmpQuery) && (
          <div className="flex items-center justify-between pt-2 border-t border-slate-100 text-xs">
            <div className="flex items-center gap-2 text-slate-500 font-medium">
              <span>Active filters: Showing <strong>{filteredRecords.length}</strong> of <strong>{records.length}</strong> records</span>
            </div>
            <button
              onClick={() => {
                setSelectedDateFilter('');
                setSelectedDeptFilter('ALL');
                setSelectedStatusFilter('ALL');
                setSearchEmpQuery('');
              }}
              className="text-indigo-600 hover:text-indigo-800 font-semibold text-xs hover:underline"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {/* Clean Attendance Table */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto max-h-[500px]">
          <table className="w-full text-sm text-left border-collapse">
            <thead className="bg-slate-50 text-xs font-bold text-slate-500 uppercase tracking-wider sticky top-0 border-b border-slate-200 z-10">
              <tr>
                <th className="px-6 py-3.5">Date</th>
                <th className="px-6 py-3.5">Emp ID</th>
                <th className="px-6 py-3.5">Employee Name</th>
                <th className="px-6 py-3.5">Department</th>
                <th className="px-6 py-3.5">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-700">
              {loading ? (
                <tr>
                  <td colSpan={5} className="py-12 text-center text-slate-400 text-sm">
                    <div className="flex items-center justify-center gap-2">
                      <FiRefreshCw className="animate-spin" /> Loading attendance records...
                    </div>
                  </td>
                </tr>
              ) : filteredRecords.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-16 text-center text-slate-400 text-sm">
                    <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-2 border border-dashed border-slate-200">
                      <FiBarChart2 className="text-slate-300 text-xl" />
                    </div>
                    No attendance records match the selected filters.
                  </td>
                </tr>
              ) : (
                filteredRecords.map((r, i) => {
                  const STATUS_MAP = {
                    PRESENT:          { label: 'Present',        color: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
                    HALF_PRESENT:     { label: 'Half Present',   color: 'text-amber-700 bg-amber-50 border-amber-200' },
                    ABSENT:           { label: 'Absent',         color: 'text-rose-700 bg-rose-50 border-rose-200' },
                    WEEK_OFF:         { label: 'Week Off',       color: 'text-slate-600 bg-slate-100 border-slate-200' },
                    WEEK_OFF_PRESENT: { label: 'WO Present',     color: 'text-blue-700 bg-blue-50 border-blue-200' },
                    LEAVE:            { label: 'Leave',          color: 'text-purple-700 bg-purple-50 border-purple-200' },
                    HOLIDAY:          { label: 'Holiday',        color: 'text-indigo-700 bg-indigo-50 border-indigo-200' },
                  };
                  const rawStatus = r.status || 'UNKNOWN';
                  const statusKey = rawStatus.length > 20 ? 'INVALID' : rawStatus.toUpperCase().replace(/ /g, '_');
                  const ui = STATUS_MAP[statusKey] || {
                    label: rawStatus.length > 20 ? 'Invalid' : rawStatus,
                    color: 'text-gray-600 bg-gray-100 border-gray-200'
                  };
                  return (
                    <tr key={r.id || i} className="hover:bg-slate-50/80 transition-colors">
                      <td className="px-6 py-3 font-medium text-slate-900 whitespace-nowrap">{fmtDate(r.attendanceDate)}</td>
                      <td className="px-6 py-3 font-mono text-xs text-slate-500 font-semibold">{r.attendanceId}</td>
                      <td className="px-6 py-3 font-semibold text-slate-800">{r.employeeName || '—'}</td>
                      <td className="px-6 py-3 text-xs text-slate-500">{r.department || '—'}</td>
                      <td className="px-6 py-3">
                        <span className={`inline-block px-2.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${ui.color}`}>
                          {ui.label}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>


    </div>
  );
}

/* ── Main Page ──────────────────────────────────────────── */
const HrAttendance = () => {
  const [historyKey, setHistoryKey] = useState(0);

  const handleUploaded = () => setHistoryKey(k => k + 1);

  return (
    <div className="attendance-container animate-fade-in space-y-6">
      <div className="attendance-header">
        <div className="header-content">
          <h1 className="page-title">Attendance Management</h1>
          <p className="page-subtitle">Import eSSL monthly attendance reports and view monthly statistics.</p>
        </div>
      </div>

      {/* Upload panel */}
      <div className="glass-panel" style={{ padding: '1.25rem' }}>
        <MonthlyUploadPanel onUploaded={handleUploaded} />
      </div>

      {/* Admin Monthly Summary & Records Viewer */}
      <MonthlyAttendanceViewer refreshKey={historyKey} />

      {/* Import History */}
      <div>
        <h2 className="text-sm font-semibold text-slate-600 mb-3 flex items-center gap-2">
          <FiClock size={14} /> Recent Uploads
        </h2>
        <ImportHistory refresh={historyKey} />
      </div>
    </div>
  );
};

export default HrAttendance;

